/**
 * Zielvermerk (Paket D, 21.10.2026) — which of several elements a patch means.
 *
 * WHY. The selector of a finding is also its identity (merge key between the
 * static and the runtime arm, `violations.id`), so it cannot be made unique
 * after the fact. `generateSelector` writes `#id` for EVERY element with an id;
 * when the id occurs twice, `#id` matches both, and a patch for the second
 * element landed on the first (querySelector) or on both (querySelectorAll —
 * how the snippet applies). F77 is the visible case; every rule on such an
 * element had the same defect. A pure position path is no way out either: on
 * DOM drift it points at a foreign element.
 *
 * WHAT. When the selector of a finding matched more than one element on the
 * scanned page and the finding meant exactly ONE of them, the scanner records
 *   i  — its position among `document.querySelectorAll(selector)` (document
 *        order, light DOM — exactly what the scanner queried),
 *   n  — how many elements the selector matched,
 *   fp — `targetFingerprint(element)`.
 * The snippet applies such a patch only when, at apply time, the selector
 * matches exactly `n` elements AND the `i`-th of them has the same
 * fingerprint. Otherwise it skips the patch silently: no fix beats a wrong fix.
 *
 * THE FINGERPRINT (decided 21.10.2026): tag name, `type`, `name`, `role` and
 * the first 64 characters of the whitespace-collapsed text, hashed (FNV-1a,
 * 32 bit). Deliberately NOT `class` and NOT `id`:
 *  - classes are the most volatile attribute on a live page — frameworks add
 *    state classes after hydration (`is-active`, `hydrated`), CSS-in-JS hashes
 *    change with every build — and the static arm reads them BEFORE any script
 *    ran. A class in the fingerprint would make the snippet drop correct fixes.
 *  - the id is what the selector already pins (and what the F77 fix changes).
 * Index and count catch structural drift; the fingerprint is the second lock
 * against a different element sliding into the slot. Two truly identical
 * twins have the same fingerprint — there the index decides, guarded by `n`.
 *
 * ⚠️ `targetFingerprint` is serialized with `.toString()` into the runtime
 * scan (scanner runtime/detectors/target-note.script.ts) and bundled into the
 * snippet. It must stay SELF-CONTAINED: no imports, no module-level helpers or
 * constants, nothing the compiler would hoist out of the function body.
 */

/** Where the meant element sat among the selector's matches at scan time. */
export interface TargetNote {
  /** 0-based position among `document.querySelectorAll(selector)`. */
  i: number;
  /** How many elements the selector matched at scan time (>= 2). */
  n: number;
  /** `targetFingerprint(element)` at scan time. */
  fp: string;
}

/** Hard caps — the note travels through a public upload path. */
export const TARGET_NOTE_MAX_N = 10000;
export const TARGET_FP_PATTERN = /^[a-z0-9-]{1,32}:[0-9a-z]{1,7}$/;

/**
 * Fingerprint of an element (see the header for what is in it and why).
 * Format `<tag>:<hash36>`, e.g. `input:1x9k2pa`. Self-contained on purpose.
 */
export function targetFingerprint(el: Element): string {
  const tag = String(el && el.tagName ? el.tagName : '').toLowerCase().slice(0, 32).replace(/[^a-z0-9-]/g, '-') || 'x';
  const parts: string[] = [tag];
  const names = ['type', 'name', 'role'];
  for (let k = 0; k < names.length; k++) {
    const v = el.getAttribute(names[k]);
    parts.push(v == null ? '' : String(v).replace(/\s+/g, ' ').trim().toLowerCase());
  }
  parts.push(String(el.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 64));
  const s = parts.join('\u0001');
  let h = 0x811c9dc5;
  for (let c = 0; c < s.length; c++) {
    h ^= s.charCodeAt(c);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return tag + ':' + (h >>> 0).toString(36);
}

/** Structural check for a note that came over a wire (zod, stored JSON). */
export function isTargetNote(x: unknown): x is TargetNote {
  if (!x || typeof x !== 'object') return false;
  const t = x as Record<string, unknown>;
  return typeof t.i === 'number' && Number.isInteger(t.i) && t.i >= 0
    && typeof t.n === 'number' && Number.isInteger(t.n) && t.n >= 2 && t.n <= TARGET_NOTE_MAX_N
    && t.i < t.n
    && typeof t.fp === 'string' && TARGET_FP_PATTERN.test(t.fp);
}

/**
 * The element a note means among the current `matches` (document order), or
 * null when the page no longer looks like the scanned one: another count, or
 * a different element at that position.
 */
export function resolveTarget<T extends Element>(matches: ArrayLike<T>, note: TargetNote): T | null {
  if (!isTargetNote(note) || matches.length !== note.n) return null;
  const el = matches[note.i];
  if (!el || targetFingerprint(el) !== note.fp) return null;
  return el;
}

/**
 * The note for `element` under `selector`, or undefined when the selector is
 * unambiguous (matches one element), does not match the element, or throws.
 * Callers decide WHETHER a finding gets a note (only when it means one of
 * several matches — see the scanner's annotate step); this only measures.
 */
export function measureTarget(doc: Document, selector: string, element: Element): TargetNote | undefined {
  let matches: NodeListOf<Element>;
  try {
    matches = doc.querySelectorAll(selector);
  } catch {
    return undefined;
  }
  if (matches.length < 2 || matches.length > TARGET_NOTE_MAX_N) return undefined;
  const i = Array.prototype.indexOf.call(matches, element) as number;
  if (i < 0) return undefined;
  return { i, n: matches.length, fp: targetFingerprint(element) };
}

/**
 * F77 (Paket D): the two occurrences of a duplicated id and the customer's
 * choice which one KEEPS it — the other gets `newId`. Carried on the patch
 * (internal fields only, never sent to visitors); the patch's own `target`
 * always points at the occurrence that is renamed.
 */
export interface IdKeepOption {
  /** 0-based occurrence index among the elements with this id (= the `#id` matches). */
  i: number;
  fp: string;
  /** outerHTML excerpt (≤ 300 chars) — page content, render as text only. */
  html: string;
}

export interface IdKeepChoice {
  id: string;
  newId: string;
  /** Exactly two, document order. */
  options: IdKeepOption[];
  /** `i` of the option the engine suggests keeps the id. */
  suggested: number;
  /** `i` chosen on confirmation (absent while pending). */
  keep?: number;
}
